import React, { useState, useEffect } from "react";
import { Drawer, Tooltip, Accordion } from "flowbite-react";
import { Timeline } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment-timezone";
import { clog } from "../lib/utils";
import UpgradeModal from "./UpgradeModal";

const DetailsDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  drawerData,
  setDrawerData,
  drawerLoading = false,
  activeTab = null, // "personal" or "company" or null - if null, always show visitor tab
}) => {
  const navigate = useNavigate();
  // Helper to format milliseconds into HH:MM:SS
  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  };

  // Helper function to get latest updatedAt from array
  const getLatestUpdatedAt = (data) => {
    if (data?.length > 0) {
      let latestDate = data[0]?.updatedAt;
      for (let i = 1; i < data?.length; i++) {
        if (moment(data[i]?.updatedAt).isAfter(latestDate)) {
          latestDate = data[i]?.updatedAt;
        }
      }
      return latestDate;
    }
    return null;
  };

  // Helper function to format time label for timeline
  const formatTimeLabel = (data, date) => {
    let isDateSame =
      moment(data?.at(0)?.createdAt).utc().format("DD-MM-YYYY") ==
      moment(data?.at(-1)?.createdAt).utc().format("DD-MM-YYYY");
    if (isDateSame) {
      return moment(date).utc().format("hh:mm A");
    }
    return moment(date).utc().format("DD/MM/YYYY hh:mm A");
  };

  // Icon component to use SVG symbols
  const Icon = ({ name, className }) => (
    <svg className={className}>
      <use href={`#icon_${name}`} />
    </svg>
  );

  // User Avatar Component
  const UserAvatar = ({ name, url }) => {
    const [imgError, setImgError] = useState(false);

    // If URL is provided and there's no load error, render the image
    if (url && !imgError) {
      return (
        <img
          src={url}
          alt={name}
          className="w-[110px] h-[110px] rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      );
    }

    // Fallback to initials
    const initials = name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "";

    return (
      <div className="w-[110px] h-[110px] rounded-full bg-gradient-to-br from-[#5e78f4] to-[#f14ce2] flex items-center justify-center text-white text-[32px] font-bold !p-0">
        {initials}
      </div>
    );
  };

  // Social icons and link component
  const SocialLink = ({ platform, socialObj, subscription_type }) => {
    const url =
      socialObj?.social?.[platform] ||
      socialObj?.social?.[platform + "Url"] ||
      socialObj?.[platform] ||
      socialObj?.[platform + "Url"];
    if (!url || url == "false") return null;

    const isCompany = socialObj?.name;

    // If should hide data and not company, show tooltip and handle click
    if (subscription_type != "paid" && !isCompany) {
      return (
        <Tooltip
          content="Please upgrade to open social profiles"
          trigger="hover"
          style="light"
          placement="top"
        >
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsUpgradeModalOpen(true);
            }}
            className="blur-sm cursor-pointer"
          >
            <Icon name={platform} className="w-5 h-5 text-[#2a2a2a]" />
          </div>
        </Tooltip>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-75 transition-opacity"
      >
        <Icon name={platform} className="w-5 h-5 text-[#2a2a2a]" />
      </a>
    );
  };
  const [activeDrawerTab, setActiveDrawerTab] = useState(
    activeTab === "personal"
      ? "visitor"
      : activeTab === "company"
      ? "company"
      : "visitor"
  );

  // Upgrade Modal state
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // User Journey state
  const [userJourneyData, setUserJourneyData] = useState([]);
  const [loadingUserJourney, setLoadingUserJourney] = useState(false);
  // clog(drawerData?.user_uniq_id, "drawerData");
  const user_uniq_id =
    drawerData?.user_uniq_id ||
    (drawerData?.useranalytic?.length > 0
      ? drawerData?.useranalytic[0]?.user_uniq_id
      : null);

  // Fetch user journey data
  const getUserJourney = async (user_uniq_id) => {
    if (!user_uniq_id) {
      setUserJourneyData([]);
      return;
    }

    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      setLoadingUserJourney(true);
      const response = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/analytics/report/details`,
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          user_uniq_id: user_uniq_id,
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      });
      clog(response, "response");
      if (!response?.data?.error) {
        let data = [];

        if (response?.data?.userjourney) {
          // Group by session_id
          let dataObj = {};
          response?.data?.userjourney?.forEach((item) => {
            if (dataObj[item?.session_id]) {
              dataObj[item?.session_id].push(item);
            } else {
              dataObj[item?.session_id] = [item];
            }
          });

          data = Object.keys(dataObj).map((key, index) => {
            return {
              session_id: key,
              session_number: index + 1,
              data: dataObj[key],
            };
          });
        }

        clog(data, "data");
        setUserJourneyData(data.reverse());
      } else {
        setUserJourneyData([]);
      }
    } catch (error) {
      if (error?.response?.status == 401 || error?.response?.status == 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      clog(error, "error");
      setUserJourneyData([]);
    } finally {
      setLoadingUserJourney(false);
    }
  };

  // Fetch user journey when drawer opens and session_id is available
  useEffect(() => {
    if (isDrawerOpen && user_uniq_id) {
      getUserJourney(user_uniq_id);
    } else {
      // Reset user journey data when drawer closes or session_id is not available
      setUserJourneyData([]);
      setLoadingUserJourney(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, drawerData]);

  // Helper function to scroll to section
  const scrollToSection = (sectionId) => {
    setActiveDrawerTab(sectionId);
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Sample data - in real app this would come from drawerData prop
  const visitorData = drawerData?.exactPerson || {};

  const companyData = drawerData?.company || {};

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div>
      {/* Visitor Section Skeleton */}
      <section className="mb-[50px]">
        <div className="flex items-center gap-2.5 mb-[30px]">
          <div className="w-[15px] h-5 bg-gray-300 rounded skeleton-wave"></div>
          <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
        </div>
        <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
          <div className="flex items-center mb-[30px] gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
            {/* Avatar skeleton */}
            <div className="w-[110px] h-[110px] rounded-full bg-gray-300 skeleton-wave"></div>

            {/* Basic info skeleton */}
            <div className="flex flex-col gap-2.5">
              <div className="h-6 w-48 bg-gray-300 rounded skeleton-wave"></div>
              <div className="flex items-center gap-[8px]">
                <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            </div>

            {/* Additional info skeletons */}
            <div className="flex flex-col gap-2.5">
              <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
            </div>
            <div className="flex flex-col gap-2.5">
              <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-6 w-40 bg-gray-300 rounded skeleton-wave"></div>
            </div>
            <div className="flex flex-col gap-2.5">
              <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-6 w-36 bg-gray-300 rounded skeleton-wave"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

      {/* Company Section Skeleton */}
      <section className="mb-[50px]">
        <div className="flex items-center gap-2.5 mb-[30px]">
          <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
          <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
        </div>
        <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
          <div className="flex items-center mb-[30px] gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
            <div className="w-[110px] h-[110px] rounded-full bg-gray-300 skeleton-wave"></div>
            <div className="flex flex-col gap-2.5 flex-1">
              <div className="h-6 w-48 bg-gray-300 rounded skeleton-wave"></div>
              <div className="flex items-center gap-[30px]">
                <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

      {/* Page Visits Section Skeleton - Accordion Style */}
      <section className="mb-[50px]">
        <div className="flex items-center gap-2.5 mb-[30px]">
          <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
          <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
        </div>
        <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-white border border-[#e9ebf0] rounded-[20px] p-5"
              >
                {/* Accordion Title Skeleton */}
                <div className="flex justify-between w-full mb-4">
                  <div className="h-6 w-24 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="h-6 w-20 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="h-6 w-40 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="h-6 w-40 bg-gray-300 rounded skeleton-wave"></div>
                </div>
                {/* Timeline Items Skeleton */}
                <div className="space-y-3 pl-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="flex gap-4">
                      <div className="h-4 w-20 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="flex-1">
                        <div className="h-4 w-full bg-gray-300 rounded skeleton-wave mb-2"></div>
                        <div className="h-4 w-3/4 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  // Determine if visitor tab should be shown
  const showVisitorTab = activeTab === null || activeTab === "personal";

  // Check if visitor data exists (has meaningful data)
  const hasVisitorData =
    visitorData &&
    (visitorData?.firstName ||
      visitorData?.lastName ||
      visitorData?.businessEmail ||
      visitorData?.personalEmail ||
      visitorData?.phone ||
      visitorData?.position);

  // Check if company data exists (has meaningful data)
  const hasCompanyData =
    companyData &&
    (companyData?.name ||
      companyData?.sector ||
      companyData?.city ||
      companyData?.state ||
      companyData?.country ||
      companyData?.yearFounded ||
      companyData?.employeesRange ||
      companyData?.revenueRange ||
      companyData?.industry);

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={() => {
        setIsDrawerOpen(false);
        setDrawerData(null);
      }}
      position="right"
      className="p-0 w-4/5 notification-canvas"
    >
      <div className="h-full flex flex-col bg-white">
        {/* Header with Tabs */}
        <div className="sticky flex top-0 z-10 bg-white border-b border-[#dcdcdc] h-[90px] items-center">
          {/* Tab Navigation */}
          <div className="flex flex-1 items-center h-full">
            {showVisitorTab && hasVisitorData && (
              <>
                {/* Visitor Tab */}
                <button
                  onClick={() => scrollToSection("visitor")}
                  className={`flex items-center gap-2.5 h-full px-10 ${
                    activeDrawerTab === "visitor"
                      ? "text-[#097ba9] bg-[#E9F2FF]"
                      : "text-[#2a2a2a]"
                  } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
                >
                  <Icon name="drawer_visitor" className="w-4 h-4" />
                  <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                    Visitor
                  </span>
                </button>
              </>
            )}

            {/* Company Tab */}
            {hasCompanyData && (
              <button
                onClick={() => scrollToSection("company")}
                className={`flex items-center gap-2.5 h-full px-10 ${
                  activeDrawerTab === "company"
                    ? "text-[#097ba9] bg-[#E9F2FF]"
                    : "text-[#2a2a2a]"
                } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
              >
                <Icon name="company" className="w-4 h-4" />
                <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                  Company
                </span>
              </button>
            )}

            {/* Page Visits Tab */}
            <button
              onClick={() => scrollToSection("pageVisits")}
              className={`flex items-center gap-2.5 h-full px-10 ${
                activeDrawerTab === "pageVisits"
                  ? "text-[#097ba9] bg-[#E9F2FF]"
                  : "text-[#2a2a2a]"
              } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
            >
              <Icon name="visits" className="w-4 h-4" />
              <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                Page Visits
              </span>
            </button>

            {/* Activity Tab */}
            {/* <button
              onClick={() => scrollToSection("activity")}
              className={`flex items-center gap-2.5 h-full px-10 ${
                activeDrawerTab === "activity"
                  ? "text-[#097ba9] bg-[#E9F2FF]"
                  : "text-[#2a2a2a]"
              } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
            >
              <Icon name="drawer_activity" className="w-4 h-4" />
              <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                Activity
              </span>
            </button> */}
          </div>
          {/* Close Button */}
          <button
            onClick={() => {
              setIsDrawerOpen(false);
              setDrawerData(null);
            }}
            className=" w-10 h-10 flex items-center justify-center text-[#2a2a2a] hover:bg-[#f0f0f0] rounded-full transition-colors duration-200 mr-2"
          >
            <Icon name="close" className="w-10 h-10" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto px-[60px] py-[40px]">
          {drawerLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              {showVisitorTab && hasVisitorData && (
                <>
                  {/* Visitor Section */}
                  <section id="visitor" className="mb-[50px]">
                    <div className="flex items-center gap-2.5 mb-[30px]">
                      <Icon
                        name="drawer_visitor"
                        className="w-[15px] h-5 text-[#000000]"
                      />
                      <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                        Visitors
                      </h2>
                      {/* Signal Source Badges */}
                      {(() => {
                        const topics = drawerData?.IntentTopics || drawerData?.intentTopics || [];
                        const signalTopics = topics.filter(t => t?.topicName);
                        if (signalTopics.length === 0) return null;
                        return (
                        <div className="flex flex-wrap items-center gap-2 ml-3">
                          {signalTopics.map((topic, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold tracking-wide"
                              style={{
                                background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                                color: '#ffffff',
                                border: '1px solid rgba(124, 58, 237, 0.3)',
                                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.25)',
                              }}
                            >
                              <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                              >
                                <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641l2.5-8.5z"/>
                              </svg>
                              {topic.topicName}
                            </span>
                          ))}
                        </div>
                        );
                      })()}
                    </div>

                    <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                      <div className="flex  items-center mb-[30px]  gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
                        {/* User Avatar */}
                        <UserAvatar
                          name={
                            [visitorData?.firstName, visitorData?.lastName]
                              .filter(Boolean)
                              .join(" ") || ""
                          }
                          url={visitorData?.picture}
                        />

                        {/* Basic Info */}
                        <div className="flex flex-col gap-2.5 ">
                          <h3
                            style={{
                              display:
                                visitorData?.firstName || visitorData?.lastName
                                  ? "block"
                                  : "none",
                            }}
                            className="font-bold font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#2a2a2a]"
                          >
                            {[visitorData?.firstName, visitorData?.lastName]
                              .filter(Boolean)
                              .join(" ") || <>&nbsp;</>}
                          </h3>
                          <div className="flex items-center gap-[8px]">
                            <SocialLink
                              subscription_type={drawerData?.subscription_type}
                              platform="linkedin"
                              socialObj={visitorData}
                            />

                            <SocialLink
                              subscription_type={drawerData?.subscription_type}
                              platform="twitter"
                              socialObj={visitorData}
                            />

                            <SocialLink
                              subscription_type={drawerData?.subscription_type}
                              platform="facebook"
                              socialObj={visitorData}
                            />
                          </div>
                        </div>

                        {/* Vertical Divider */}
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* ICP Score */}
                        <div
                          style={{
                            display: drawerData?.icpScore ? "block" : "none",
                          }}
                          className="flex flex-col gap-[5px] "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="icp_score"
                              className="w-6 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              ICP
                            </span>
                          </div>
                          {drawerData?.subscription_type != "paid" ? (
                            <Tooltip
                              trigger="hover"
                              autoFocus={true}
                              style={"light"}
                              content="Please upgrade to view ICP Score details"
                            >
                              <div
                                className={`flex items-center gap-2.5 blur-sm cursor-pointer`}
                                onClick={() => {
                                  setIsUpgradeModalOpen(true);
                                }}
                              >
                                <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                                  ICP&nbsp;
                                </span>

                                <Icon
                                  name="information"
                                  className="w-5 h-5 hidden"
                                />
                              </div>
                            </Tooltip>
                          ) : (
                            <div className={`flex items-center gap-2.5`}>
                              <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00]">
                                {drawerData?.icpScore || <>&nbsp;</>}
                                {/* {drawerData?.icpScore || <>&nbsp;</>} */}
                              </span>
                              <Tooltip
                                // className="hidden"
                                trigger="hover"
                                autoFocus={true}
                                style={"light"}
                                className="w-[50%]"
                                // content={
                                //   <div className="bg-[#ffffff]  rounded-md p-2">
                                //     {/* Header */}
                                //     <div className="flex  items-center gap-2.5 mb-[25px]">
                                //       <Icon
                                //         name="matching_factors"
                                //         className="w-[26px] h-5 "
                                //       />
                                //       <span className="font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#000000] leading-none">
                                //         Matching Factors
                                //       </span>
                                //     </div>

                                //     {/* Factors Grid */}
                                //     <div className="flex flex-col gap-7">
                                //       {/* First Row */}
                                //       <div className="flex flex-wrap gap-5">
                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="position"
                                //               className="w-[15px] h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[155px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 High-value position
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 President
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>

                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="company_level"
                                //               className="w-[22px] h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[156px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Small company size
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 31+ employees
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>
                                //       </div>

                                //       {/* Second Row */}
                                //       <div className="flex flex-wrap gap-5">
                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="drawer_industry"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[121px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Industry match
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Oil & Energy
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>

                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="engagement"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[186px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 High engagement
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 8360 seconds duration
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>
                                //       </div>

                                //       {/* Third Row */}
                                //       <div className="flex gap-5">
                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="visits"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[106px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Multiple visits
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 02 Visits
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>

                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] px-[9px] py-[5px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="drawer_revenue"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-3.5 w-[211px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Medium revenue company
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 $1M - $5M
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>
                                //       </div>

                                //       {/* Fourth Row */}
                                //       <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-60 h-20">
                                //         <div className="flex gap-2.5">
                                //           <Icon
                                //             name="geography"
                                //             className="w-3.5 h-5 text-[#2a2a2a]"
                                //           />
                                //           <div className="flex flex-col gap-[9px] w-[143px]">
                                //             <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //               Target geography
                                //             </span>
                                //             <span className="ffont-bold ont-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //               United States
                                //             </span>
                                //           </div>
                                //         </div>
                                //       </div>
                                //     </div>
                                //   </div>
                                // }
                                content={
                                  "Ideal Customer Profile (ICP) score is on a scale of 0 to 100. The higher the better. Kwin compares the identified visitor with the ICP approved in the Knowledgebase section."
                                }
                              >
                                <Icon name="information" className="w-5 h-5" />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                        {/* PIS Score */}
                        <div
                          style={{
                            display: drawerData?.pisScore ? "block" : "none",
                          }}
                          className="flex flex-col gap-[5px] "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="pis_score"
                              className="w-6 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              PIS
                            </span>
                          </div>
                          {drawerData?.subscription_type != "paid" ? (
                            <Tooltip
                              trigger="hover"
                              autoFocus={true}
                              style={"light"}
                              content="Please upgrade to view PIS Score details"
                            >
                              <div
                                className={`flex items-center gap-2.5 blur-sm cursor-pointer`}
                                onClick={() => {
                                  setIsUpgradeModalOpen(true);
                                }}
                              >
                                <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                                  PIS&nbsp;
                                </span>

                                <Icon
                                  name="information"
                                  className="w-5 h-5 hidden"
                                />
                              </div>
                            </Tooltip>
                          ) : (
                            <div className={`flex items-center gap-2.5`}>
                              <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00]">
                                {drawerData?.pisScore || <>&nbsp;</>}
                              </span>
                              <Tooltip
                                // className="hidden"
                                trigger="hover"
                                autoFocus={true}
                                style={"light"}
                                className="w-[50%]"
                                // content={
                                //   <div className="bg-[#ffffff]  rounded-md p-2">
                                //     {/* Header */}
                                //     <div className="flex  items-center gap-2.5 mb-[25px]">
                                //       <Icon
                                //         name="matching_factors"
                                //         className="w-[26px] h-5 "
                                //       />
                                //       <span className="font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#000000] leading-none">
                                //         Matching Factors
                                //       </span>
                                //     </div>

                                //     {/* Factors Grid */}
                                //     <div className="flex flex-col gap-7">
                                //       {/* First Row */}
                                //       <div className="flex flex-wrap gap-5">
                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="position"
                                //               className="w-[15px] h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[155px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 High-value position
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 President
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>

                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="company_level"
                                //               className="w-[22px] h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[156px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Small company size
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 31+ employees
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>
                                //       </div>

                                //       {/* Second Row */}
                                //       <div className="flex flex-wrap gap-5">
                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="drawer_industry"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[121px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Industry match
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Oil & Energy
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>

                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="engagement"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[186px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 High engagement
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 8360 seconds duration
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>
                                //       </div>

                                //       {/* Third Row */}
                                //       <div className="flex gap-5">
                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="visits"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-[9px] w-[106px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Multiple visits
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 02 Visits
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>

                                //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] px-[9px] py-[5px] w-[260px] h-20">
                                //           <div className="flex gap-2.5">
                                //             <Icon
                                //               name="drawer_revenue"
                                //               className="w-5 h-5 text-[#2a2a2a]"
                                //             />
                                //             <div className="flex flex-col gap-3.5 w-[211px]">
                                //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 Medium revenue company
                                //               </span>
                                //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //                 $1M - $5M
                                //               </span>
                                //             </div>
                                //           </div>
                                //         </div>
                                //       </div>

                                //       {/* Fourth Row */}
                                //       <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-60 h-20">
                                //         <div className="flex gap-2.5">
                                //           <Icon
                                //             name="geography"
                                //             className="w-3.5 h-5 text-[#2a2a2a]"
                                //           />
                                //           <div className="flex flex-col gap-[9px] w-[143px]">
                                //             <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //               Target geography
                                //             </span>
                                //             <span className="ffont-bold ont-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                //               United States
                                //             </span>
                                //           </div>
                                //         </div>
                                //       </div>
                                //     </div>
                                //   </div>
                                // }
                                content={
                                  "Purchase Intent Score (PIS) is on a scale of 0 to 100. The higher the better. Kwin scores this based on the identified visitor's browsing behavior on your website."
                                }
                              >
                                <Icon name="information" className="w-5 h-5 " />
                              </Tooltip>
                            </div>
                          )}
                        </div>

                        {/* Vertical Divider */}
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* Recommendation */}
                        <div
                          style={{
                            display: visitorData.recommendation
                              ? "block"
                              : "none",
                          }}
                          className=" flex-col gap-2.5  hidden"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_recommendation"
                              className="w-5 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Recommendation
                            </span>
                          </div>
                          <p className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a] leading-normal">
                            {visitorData.recommendation || <>&nbsp;</>}
                          </p>
                        </div>
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] hidden"></div> */}

                        {/* Title */}
                        <div
                          style={{
                            display: visitorData?.position ? "block" : "none",
                          }}
                          className="flex items-center "
                        >
                          <div className="flex flex-col gap-2.5 flex-1">
                            <div className="flex items-center gap-2.5">
                              <Icon
                                name="drawer_title"
                                className="w-[23px] h-5 text-[#2a2a2a]"
                              />
                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                                Title
                              </span>
                            </div>
                            <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                              {visitorData?.position || <>&nbsp;</>}
                            </span>
                          </div>
                        </div>
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}

                        {/* Email */}
                        <div
                          style={{
                            display:
                              visitorData?.businessEmail ||
                              visitorData?.personalEmail
                                ? "block"
                                : "none",
                          }}
                          className="flex flex-col gap-2.5 "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="email"
                              className="w-5 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Email Address
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {/* <a
                              href={`mailto:${visitorData?.businessEmail}`}
                              className="text-[14px] text-[#2a2a2a] font-normal underline"
                            >
                              {visitorData?.businessEmail}
                            </a>
                            <br />
                            <a
                              href={`mailto:${visitorData?.personalEmail}`}
                              className="text-[14px] text-[#2a2a2a] font-normal underline"
                            >
                              {visitorData?.personalEmail}
                            </a> */}
                            {[
                              visitorData?.businessEmail,
                              visitorData?.personalEmail,
                            ]
                              .filter(Boolean)
                              .map((email, idx) =>
                                drawerData?.subscription_type != "paid" ? (
                                  <>
                                    <Tooltip
                                      content="Please upgrade to view email"
                                      trigger="hover"
                                      style="light"
                                      placement="top"
                                    >
                                      <div
                                        onClick={() => {
                                          setIsUpgradeModalOpen(true);
                                        }}
                                        className="blur-sm cursor-pointer"
                                      >
                                        <p
                                          key={idx}
                                          className=" text-[#2a2a2a] font-normal underline"
                                        >
                                          placeholder@email.test
                                        </p>
                                      </div>
                                      {idx < 1 && <br />}
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    <a
                                      key={idx}
                                      href={`mailto:${email}`}
                                      className=" text-[#2a2a2a] font-normal underline"
                                    >
                                      {email}
                                    </a>
                                    {idx < 1 && <br />}
                                  </>
                                )
                              )}
                          </span>
                        </div>

                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}
                        {/* Phone */}
                        <div
                          style={{
                            display: visitorData?.phone ? "block" : "none",
                          }}
                          className="flex flex-col gap-2.5 "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="phone"
                              className="w-5 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Phone
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {drawerData?.subscription_type != "paid" ? (
                              <>
                                <Tooltip
                                  content="Please upgrade to view phone number"
                                  trigger="hover"
                                  style="light"
                                  placement="top"
                                >
                                  <div
                                    onClick={() => {
                                      setIsUpgradeModalOpen(true);
                                    }}
                                    className="blur-sm cursor-pointer"
                                  >
                                    <p className=" text-[#2a2a2a] font-normal underline">
                                      +1 (555) 555-5555
                                    </p>
                                  </div>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <a
                                  href={`tel:${
                                    visitorData?.phone
                                      ? visitorData?.phone.includes("+")
                                        ? visitorData?.phone
                                        : "+" + visitorData?.phone
                                      : ""
                                  }`}
                                  className=" text-[#2a2a2a] font-normal underline"
                                >
                                  {visitorData?.phone
                                    ? visitorData?.phone.includes("+")
                                      ? visitorData?.phone
                                      : "+" + visitorData?.phone
                                    : ""}
                                </a>
                              </>
                            )}
                          </span>
                        </div>

                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* Last Seen */}
                        <div
                          style={{
                            display: drawerData?.lastSession?.date
                              ? "block"
                              : "none",
                          }}
                          className="flex flex-col gap-2.5 "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_clock"
                              className="w-[33px] h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Last Seen
                            </span>
                          </div>
                          {activeTab === "personal" ? (
                            <Tooltip
                              trigger="hover"
                              autoFocus={true}
                              style={"light"}
                              placement="right"
                              content={
                                <div className="text-left">
                                  {/* title -  Time Conversion */}
                                  <h4 className="font-bold mb-2">
                                    Time Conversion
                                  </h4>
                                  <hr />

                                  <div>
                                    <span className="font-bold">UTC:</span>{" "}
                                    {drawerData?.lastSession?.date
                                      ? moment(drawerData.lastSession.date)
                                          .utc()
                                          .format("MMMM Do YYYY, h:mm:ss a")
                                      : ""}
                                  </div>
                                  <hr />
                                  <div>
                                    <span className="font-bold">
                                      {
                                        // computers timezone name ex. IST, PST
                                        moment.tz
                                          .zone(moment.tz.guess())
                                          .abbr(new Date())
                                      }{" "}
                                      ● Your Computer:
                                    </span>{" "}
                                    {drawerData?.lastSession?.date
                                      ? moment(drawerData.lastSession.date)
                                          .local()
                                          .format("MMMM Do YYYY, h:mm:ss a z")
                                      : ""}
                                  </div>
                                </div>
                              }
                            >
                              <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                {drawerData?.lastSession?.date ? (
                                  moment(drawerData?.lastSession?.date)
                                    .utc()
                                    .format("MMMM Do YYYY, h:mm:ss a") + " UTC"
                                ) : (
                                  <>&nbsp;</>
                                )}
                                {/* icon_eye  */}
                                <svg className="w-6 h-6 inline-block ml-1">
                                  <use href={`#icon_eye`} />
                                </svg>
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                              {drawerData?.lastSession?.date ? (
                                // moment(drawerData?.lastSession?.date).format(
                                //   "MMMM Do YYYY, h:mm:ss a"
                                // )
                                moment(drawerData.lastSession.date)
                                  .utc()
                                  .format("MMMM Do YYYY, h:mm:ss a") + " UTC"
                              ) : (
                                <>&nbsp;</>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Visitor Browsing Location */}
                        <div
                          style={{
                            display:
                              [
                                drawerData?.lastSession?.city,
                                drawerData?.lastSession?.state,
                                drawerData?.lastSession?.country,
                              ].filter(Boolean).length > 0
                                ? "block"
                                : "none",
                          }}
                          className="flex flex-col gap-2.5"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_location"
                              className="w-4 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Visitor Browsing Location
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {[
                              drawerData?.lastSession?.city,
                              drawerData?.lastSession?.state,
                              drawerData?.lastSession?.country,
                            ]
                              .filter(Boolean)
                              .join(", ") || <>&nbsp;</>}
                          </span>
                        </div>

                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* {/* Contact Stage */}
                        {/* <div className="flex flex-col gap-2.5 ">
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_stage"
                              className="w-[18px] h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Contact Stage
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {visitorData.contactStage || <>&nbsp;</>}
                          </span>
                        </div>  */}
                      </div>
                    </div>
                  </section>
                  {/* Divider Line */}
                  <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>
                </>
              )}

              {/* Company Section */}
              {hasCompanyData && (
                <section id="company" className="mb-[50px]">
                  <div className="flex items-center gap-2.5 mb-[30px]">
                    <Icon name="company" className="w-5 h-5 text-[#000000]" />
                    <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                      Company
                    </h2>
                    {/* Signal Source Badges */}
                    {(() => {
                      const topics = drawerData?.IntentTopics || drawerData?.intentTopics || [];
                      const signalTopics = topics.filter(t => t?.topicName);
                      if (signalTopics.length === 0) return null;
                      return (
                      <div className="flex flex-wrap items-center gap-2 ml-3">
                        {signalTopics.map((topic, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold tracking-wide"
                            style={{
                              background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                              color: '#ffffff',
                              border: '1px solid rgba(124, 58, 237, 0.3)',
                              boxShadow: '0 2px 8px rgba(124, 58, 237, 0.25)',
                            }}
                          >
                            <svg
                              className="w-3.5 h-3.5 flex-shrink-0"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                            >
                              <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641l2.5-8.5z"/>
                            </svg>
                            {topic.topicName}
                          </span>
                        ))}
                      </div>
                      );
                    })()}
                  </div>

                  <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                    <div className="flex  items-center mb-[30px]  gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
                      {/* <div className="w-[120px] h-[120px] rounded-full overflow-hidden">
                      <img
                        src={companyData?.logo || "#"}
                        alt="Company Logo"
                        className="w-full h-full object-cover"
                      />
                    </div> */}
                      <UserAvatar
                        name={companyData?.name || ""}
                        url={companyData?.logo || ""}
                      />
                      <div className="flex flex-col gap-2.5 flex-1">
                        <h3
                          style={{
                            display: companyData?.name ? "block" : "none",
                          }}
                          className="font-bold font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#2a2a2a]"
                        >
                          {companyData?.name || <>&nbsp;</>}
                        </h3>
                        <div className="flex items-center gap-[30px]">
                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="linkedin"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="twitter"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="youtube"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="crunchbase"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="facebook"
                            socialObj={companyData}
                          />
                        </div>
                      </div>
                      {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}
                      <div
                        style={{
                          display: companyData?.sector ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 px-2.5"
                      >
                        <div className="flex items-center gap-[10px]">
                          <Icon
                            name="drawer_sector"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Sector
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.sector || <>&nbsp;</>}
                        </span>
                      </div>
                      {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}
                      <div
                        style={{
                          display:
                            companyData?.city ||
                            companyData?.state ||
                            companyData?.country
                              ? "block"
                              : "none",
                        }}
                        className="flex flex-col gap-2.5"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_location"
                            className="w-4 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Company HQ Location
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {[
                            companyData?.city,
                            companyData?.state,
                            companyData?.country,
                          ]
                            ?.filter(Boolean)
                            .join(", ") || <>&nbsp;</>}
                        </span>
                      </div>
                      {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                      <div
                        style={{
                          display: companyData?.yearFounded ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_year"
                            className="w-[19px] h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Year Founded
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.yearFounded || <>&nbsp;</>}
                        </span>
                      </div>
                      {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                      <div
                        style={{
                          display: companyData?.employeesRange
                            ? "block"
                            : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_employees"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Total Employee
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.employeesRange || <>&nbsp;</>}
                        </span>
                      </div>
                      {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                      <div
                        style={{
                          display: companyData?.revenueRange ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_revenue"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Revenue
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.revenueRange || <>&nbsp;</>}
                          {/* {companyData?.estimatedAnnualRevenue || <>&nbsp;</>} */}
                        </span>
                      </div>
                      {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                      <div
                        style={{
                          display: companyData?.industry ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 w-[124px]"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_industry"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Industry
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.industry || <>&nbsp;</>}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Divider Line - only show if there's visitor or company data before page visits */}
              {(hasVisitorData || hasCompanyData) && (
                <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>
              )}

              {/* Page Visits Section - User Journey */}
              <section id="pageVisits" className="mb-[50px]">
                <div className="flex items-center gap-2.5 mb-[30px]">
                  <Icon name="visits" className="w-5 h-5 text-[#000000]" />
                  <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                    Page Visits
                  </h2>
                </div>

                <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                  {!user_uniq_id ? (
                    // Fallback to pageVisits if session_id is not available
                    drawerData?.pageVisits &&
                    drawerData.pageVisits.length > 0 ? (
                      <div className="space-y-4">
                        {drawerData?.pageVisits.map((visit, index) => (
                          <div
                            key={index}
                            className="bg-white border border-[#e9ebf0] rounded-[20px] p-5 hover:shadow-md transition-shadow duration-200"
                          >
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              {/* Path */}
                              <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    name="globe"
                                    className="w-4 h-4 text-[#2a2a2a]"
                                  />
                                  <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                    Page Path
                                  </span>
                                </div>
                                <span className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#097ba9] break-all">
                                  {visit?.path?.includes("http")
                                    ? visit?.path
                                    : (drawerData?.workspaceName || "") +
                                      visit?.path}
                                </span>
                              </div>

                              {/* Visit Count */}
                              <div className="flex flex-col gap-2 min-w-[120px]">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    name="visits"
                                    className="w-4 h-4 text-[#2a2a2a]"
                                  />
                                  <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                    Visit Count
                                  </span>
                                </div>
                                <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                  {visit.visitCount || 0}
                                </span>
                              </div>

                              {/* Total Time */}
                              <div className="flex flex-col gap-2 min-w-[120px]">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    name="duration"
                                    className="w-4 h-4 text-[#2a2a2a]"
                                  />
                                  <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                    Total Time
                                  </span>
                                </div>
                                <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                  {formatDuration(visit.totalTime || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-[50px]">
                        <p className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#666] mb-4">
                          No user journey available
                        </p>
                        <p className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-[#999]">
                          User journey data is not available for this visitor.
                        </p>
                      </div>
                    )
                  ) : loadingUserJourney ? (
                    <>
                      <div className="UserPopAccordion">
                        <Accordion>
                          {[1, 2].map((sessionIndex) => (
                            <Accordion.Panel key={sessionIndex}>
                              <Accordion.Title>
                                <div className="flex justify-between w-full items-center flex-wrap gap-4">
                                  {/* Session Number Skeleton */}
                                  <div className="h-6 w-28 bg-gray-300 rounded skeleton-wave"></div>

                                  {/* Country/City Skeleton */}
                                  <div className="h-6 w-24 bg-gray-300 rounded skeleton-wave"></div>

                                  {/* Pages Count Skeleton */}
                                  <div className="flex items-center gap-2">
                                    <div className="w-[18px] h-[18px] bg-gray-300 rounded skeleton-wave"></div>
                                    <div className="flex flex-col gap-1">
                                      <div className="h-4 w-14 bg-gray-300 rounded skeleton-wave"></div>
                                      <div className="h-4 w-6 bg-gray-300 rounded skeleton-wave"></div>
                                    </div>
                                  </div>

                                  {/* Entry Time Skeleton */}
                                  <div className="flex items-center gap-2">
                                    <div className="w-[18px] h-[18px] bg-gray-300 rounded skeleton-wave"></div>
                                    <div className="flex flex-col gap-1">
                                      <div className="h-4 w-16 bg-gray-300 rounded skeleton-wave"></div>
                                      <div className="h-4 w-36 bg-gray-300 rounded skeleton-wave"></div>
                                    </div>
                                  </div>

                                  {/* Exit Time Skeleton */}
                                  <div className="flex items-center gap-2">
                                    <div className="w-[18px] h-[18px] bg-gray-300 rounded skeleton-wave"></div>
                                    <div className="flex flex-col gap-1">
                                      <div className="h-4 w-12 bg-gray-300 rounded skeleton-wave"></div>
                                      <div className="h-4 w-36 bg-gray-300 rounded skeleton-wave"></div>
                                    </div>
                                  </div>
                                </div>
                              </Accordion.Title>
                              <Accordion.Content>
                                <div className="my-3">
                                  {/* Timeline Skeleton */}
                                  <div className="space-y-4">
                                    {[1, 2, 3, 4].map((itemIndex) => (
                                      <div
                                        key={itemIndex}
                                        className="flex gap-4"
                                      >
                                        {/* Time Label Skeleton */}
                                        <div className="w-24 shrink-0">
                                          <div className="h-5 w-20 bg-gray-300 rounded skeleton-wave"></div>
                                        </div>

                                        {/* Content Skeleton */}
                                        <div className="flex-1 space-y-2">
                                          {/* Page URL Skeleton */}
                                          <div className="h-5 w-full bg-gray-300 rounded skeleton-wave"></div>

                                          {/* Metrics Skeleton */}
                                          <div className="flex items-center gap-3 flex-wrap">
                                            <div className="h-4 w-16 bg-gray-300 rounded skeleton-wave"></div>
                                            <div className="h-4 w-20 bg-gray-300 rounded skeleton-wave"></div>
                                            <div className="h-4 w-20 bg-gray-300 rounded skeleton-wave"></div>
                                            <div className="h-4 w-16 bg-gray-300 rounded skeleton-wave"></div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </Accordion.Content>
                            </Accordion.Panel>
                          ))}
                        </Accordion>
                      </div>
                    </>
                  ) : userJourneyData && userJourneyData.length > 0 ? (
                    <div className="UserPopAccordion">
                      <Accordion>
                        {userJourneyData.map((item, index) => (
                          <Accordion.Panel key={item?.session_id || index}>
                            <Accordion.Title>
                              <div className="flex justify-between w-full">
                                <div className="font-bold text-[20px] flex items-center">
                                  Session {item?.session_number}
                                </div>
                                <div className="font-bold text-[20px] flex items-center">
                                  {item?.data[0]?.ip_contry}
                                  <br />
                                  {item?.data[0]?.city}
                                </div>
                                <div className="flex">
                                  <svg className="icon text-[18px] me-2 mt-1">
                                    <use href="#icon_pagecopy"></use>
                                  </svg>
                                  <div className="font-bold text-[18px]">
                                    <p>Pages</p>
                                    <p>{item?.data?.length}</p>
                                  </div>
                                </div>
                                <div className="flex">
                                  <svg className="icon text-[18px] me-2 mt-1">
                                    <use href="#icon_entry"></use>
                                  </svg>
                                  <div className="font-bold text-[18px]">
                                    <p>Entry:</p>
                                    {item?.data?.at(0)?.createdAt ? (
                                      <Tooltip
                                        trigger="hover"
                                        autoFocus={true}
                                        style={"light"}
                                        placement="right"
                                        content={
                                          <div className="text-left">
                                            <h4 className="font-bold mb-2">
                                              Time Conversion
                                            </h4>
                                            <hr />
                                            <div>
                                              <span className="font-bold">
                                                UTC:
                                              </span>{" "}
                                              {moment(
                                                item?.data?.at(0)?.createdAt
                                              )
                                                .utc()
                                                .format(
                                                  "MMMM Do YYYY, h:mm:ss a"
                                                )}
                                            </div>
                                            <hr />
                                            <div>
                                              <span className="font-bold">
                                                {moment.tz
                                                  .zone(moment.tz.guess())
                                                  .abbr(new Date())}{" "}
                                                ● Your Computer:
                                              </span>{" "}
                                              {moment(
                                                item?.data?.at(0)?.createdAt
                                              )
                                                .local()
                                                .format(
                                                  "MMMM Do YYYY, h:mm:ss a z"
                                                )}
                                            </div>
                                          </div>
                                        }
                                      >
                                        <span className="cursor-pointer">
                                          {moment(item?.data?.at(0)?.createdAt)
                                            .utc()
                                            .format("MMM DD, YYYY | hh:mm A") +
                                            " UTC"}
                                          <svg className="w-4 h-4 inline-block ml-1">
                                            <use href={`#icon_eye`} />
                                          </svg>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <span></span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex">
                                  <svg className="icon text-[18px] me-2 mt-1">
                                    <use href="#icon_exit"></use>
                                  </svg>
                                  <div className="font-bold text-[18px]">
                                    <p>Exit: </p>
                                    {getLatestUpdatedAt(item?.data) ? (
                                      <Tooltip
                                        trigger="hover"
                                        autoFocus={true}
                                        style={"light"}
                                        placement="right"
                                        content={
                                          <div className="text-left">
                                            <h4 className="font-bold mb-2">
                                              Time Conversion
                                            </h4>
                                            <hr />
                                            <div>
                                              <span className="font-bold">
                                                UTC:
                                              </span>{" "}
                                              {moment(
                                                getLatestUpdatedAt(item?.data)
                                              )
                                                .utc()
                                                .format(
                                                  "MMMM Do YYYY, h:mm:ss a"
                                                )}
                                            </div>
                                            <hr />
                                            <div>
                                              <span className="font-bold">
                                                {moment.tz
                                                  .zone(moment.tz.guess())
                                                  .abbr(new Date())}{" "}
                                                ● Your Computer:
                                              </span>{" "}
                                              {moment(
                                                getLatestUpdatedAt(item?.data)
                                              )
                                                .local()
                                                .format(
                                                  "MMMM Do YYYY, h:mm:ss a z"
                                                )}
                                            </div>
                                          </div>
                                        }
                                      >
                                        <span className="cursor-pointer">
                                          {moment(
                                            getLatestUpdatedAt(item?.data)
                                          )
                                            .utc()
                                            .format("MMM DD, YYYY | hh:mm A") +
                                            " UTC"}
                                          <svg className="w-4 h-4 inline-block ml-1">
                                            <use href={`#icon_eye`} />
                                          </svg>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <span></span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Accordion.Title>
                            <Accordion.Content>
                              <div>
                                {/* Referrer Source only show if referrer is not empty */}
                                {item?.data?.at(0)?.referrer && (
                                  <div className="mb-4">
                                    <span className="font-bold text-[16px]">
                                      Referrer Source :{" "}
                                    </span>
                                    <a
                                      href={item?.data?.at(0)?.referrer}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-blue-500"
                                    >
                                      {item?.data?.at(0)?.referrer}
                                    </a>
                                  </div>
                                )}
                                <Timeline
                                  className="my-3"
                                  mode={"left"}
                                  items={
                                    item?.data?.map((link, linkIndex) => {
                                      return {
                                        label: (
                                          <Tooltip
                                            trigger="hover"
                                            autoFocus={true}
                                            style={"light"}
                                            placement="right"
                                            content={
                                              <div className="text-left">
                                                <h4 className="font-bold mb-2">
                                                  Time Conversion
                                                </h4>
                                                <hr />
                                                <div>
                                                  <span className="font-bold">
                                                    UTC:
                                                  </span>{" "}
                                                  {moment(link?.createdAt)
                                                    .utc()
                                                    .format(
                                                      "MMMM Do YYYY, h:mm:ss a"
                                                    )}
                                                </div>
                                                <hr />
                                                <div>
                                                  <span className="font-bold">
                                                    {moment.tz
                                                      .zone(moment.tz.guess())
                                                      .abbr(new Date())}{" "}
                                                    ● Your Computer:
                                                  </span>{" "}
                                                  {moment(link?.createdAt)
                                                    .local()
                                                    .format(
                                                      "MMMM Do YYYY, h:mm:ss a z"
                                                    )}
                                                </div>
                                              </div>
                                            }
                                          >
                                            <span className="cursor-pointer">
                                              {formatTimeLabel(
                                                item?.data,
                                                link?.createdAt
                                              )}
                                              {linkIndex == 0 && (
                                                <svg className="icon text-[16px] ms-2">
                                                  <use href="#icon_entry"></use>
                                                </svg>
                                              )}
                                              {linkIndex ==
                                                item?.data?.length - 1 && (
                                                <svg className="icon text-[16px] ms-2">
                                                  <use href="#icon_exit"></use>
                                                </svg>
                                              )}
                                              {/* <svg className="w-4 h-4 inline-block ml-1">
                                                <use href={`#icon_eye`} />
                                              </svg> */}
                                            </span>
                                          </Tooltip>
                                        ),
                                        children: (
                                          <>
                                            <a
                                              href={link?.page_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {link?.page_url}
                                            </a>
                                            <div className="flex items-center">
                                              <span title="Time Spent">
                                                <svg className="icon text-[16px]">
                                                  <use href="#icon_clock"></use>
                                                </svg>
                                                {link?.time_spent || 0} Sec,
                                              </span>
                                              <span title="Scroll Depth">
                                                <svg className="icon text-[16px]">
                                                  <use href="#icon_mouse"></use>
                                                </svg>
                                                {link?.scroll_depth || 0}%,{" "}
                                              </span>
                                              <span title="Page View Count">
                                                <svg className="icon text-[16px]">
                                                  <use href="#icon_eye"></use>
                                                </svg>
                                                {link?.count},
                                              </span>
                                              <span title="Links Clicked">
                                                <svg className="icon text-[16px]">
                                                  <use href="#icon_cursor"></use>
                                                </svg>
                                                {link?.links_clicked?.length ||
                                                  0}
                                                {link?.links_clicked?.length >
                                                  0 && ","}
                                              </span>
                                              {link?.links_clicked?.map(
                                                (linkClicked, clickedIndex) => (
                                                  <>
                                                    <a
                                                      href={linkClicked?.link}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                    >
                                                      {linkClicked?.link}
                                                    </a>
                                                    {clickedIndex !==
                                                      link?.links_clicked
                                                        ?.length -
                                                        1 && ", "}
                                                  </>
                                                )
                                              )}
                                            </div>
                                          </>
                                        ),
                                      };
                                    }) || []
                                  }
                                />
                              </div>
                            </Accordion.Content>
                          </Accordion.Panel>
                        ))}
                      </Accordion>
                    </div>
                  ) : (
                    <div className="text-center py-[50px]">
                      <p className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#666] mb-4">
                        No user journey data available
                      </p>
                      <p className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-[#999]">
                        User journey data will appear here once available.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Activity Section */}
              {/* <section id="activity" className="mb-[50px]">
                <div className="flex items-center gap-2.5 mb-[30px]">
                  <Icon
                    name="drawer_activity"
                    className="w-5 h-5 text-[#000000]"
                  />
                  <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                    Activity
                  </h2>
                </div>

                <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                  <div className="text-center py-[50px]">
                    <p className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#666] mb-4">
                      Activity tracking coming soon...
                    </p>
                    <p className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-[#999]">
                      This section will show visitor activity, page views, and
                      engagement metrics.
                    </p>
                  </div>
                </div>
              </section> */}
            </>
          )}
        </div>
      </div>
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />
    </Drawer>
  );
};

export default DetailsDrawer;
